import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Check, X, Loader2, RotateCcw, FileText, Search, HelpCircle, Tags } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = '/api';

const EnrichmentModal = ({ listing, onClose, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [enrichment, setEnrichment] = useState(null);
  const [applying, setApplying] = useState(false);
  const [step, setStep] = useState('confirm'); // confirm | preview | done

  const generatePreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/listings/${listing.id}/enrich`);
      setEnrichment(res.data.enrichment);
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate enrichment');
    } finally {
      setLoading(false);
    }
  };

  const applyEnrichment = async () => {
    if (!enrichment) return;
    setApplying(true);
    try {
      await axios.post(`${API}/admin/listings/${listing.id}/enrich/apply`, enrichment);
      toast.success('Enrichment applied successfully');
      setStep('done');
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply enrichment');
    } finally {
      setApplying(false);
    }
  };

  const regenerate = () => {
    setEnrichment(null);
    generatePreview();
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="enrichment-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex items-center justify-center min-h-full p-4 pointer-events-none">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-800">AI SEO Enrichment</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="enrichment-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <h3 className="font-medium text-slate-800 mb-2">What this does:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><FileText className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Enhances the listing description with SEO-optimised copy</li>
                  <li className="flex items-start gap-2"><Search className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Generates an optimised meta title & description for Google</li>
                  <li className="flex items-start gap-2"><HelpCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Creates FAQ schema (shows as rich results on Google)</li>
                  <li className="flex items-start gap-2"><Tags className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Suggests relevant SEO keywords</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border">
                <p className="text-sm text-slate-500 mb-1">Listing to enrich:</p>
                <p className="font-medium text-slate-800">{listing.title}</p>
                <p className="text-sm text-slate-500">{listing.town} &middot; {listing.category}</p>
                {listing.description && (
                  <p className="text-xs text-slate-400 mt-2 line-clamp-2">Current: {listing.description.substring(0, 150)}...</p>
                )}
              </div>
              <p className="text-xs text-slate-400">You'll see a preview before anything is saved. The original description is preserved as a backup.</p>
            </div>
          )}

          {step === 'preview' && enrichment && (
            <div className="space-y-5">
              {/* Enhanced Description */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-indigo-500" />
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Enhanced Description</h3>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {enrichment.enhanced_description}
                </div>
              </div>

              {/* Meta Title & Description */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Search className="h-4 w-4 text-indigo-500" />
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Google Preview</h3>
                </div>
                <div className="bg-white border rounded-lg p-4 space-y-1">
                  <p className="text-blue-700 text-base font-medium hover:underline cursor-default">{enrichment.seo_title}</p>
                  <p className="text-green-700 text-xs">discoveressex.co.uk/listing/{listing.town_slug}/...</p>
                  <p className="text-sm text-slate-600">{enrichment.seo_description}</p>
                </div>
              </div>

              {/* FAQs */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle className="h-4 w-4 text-indigo-500" />
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">FAQ Rich Results</h3>
                </div>
                <div className="space-y-2">
                  {enrichment.seo_faqs?.map((faq, i) => (
                    <div key={i} className="bg-slate-50 border rounded-lg p-3">
                      <p className="text-sm font-medium text-slate-800">{faq.question}</p>
                      <p className="text-sm text-slate-600 mt-1">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Keywords */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tags className="h-4 w-4 text-indigo-500" />
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">SEO Keywords</h3>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {enrichment.seo_keywords?.map((kw, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{kw}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Enrichment Applied</h3>
              <p className="text-sm text-slate-500">The listing has been updated with SEO-optimised content.<br />Changes will reflect on the live page shortly.</p>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-10 w-10 text-indigo-500 animate-spin mb-4" />
              <p className="text-sm text-slate-500">Generating SEO enrichment...</p>
              <p className="text-xs text-slate-400 mt-1">This may take a few seconds</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between gap-3">
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="enrichment-cancel-btn">Cancel</Button>
              <Button onClick={generatePreview} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="enrichment-generate-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate Preview
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={regenerate} disabled={loading} data-testid="enrichment-regenerate-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Regenerate
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} data-testid="enrichment-discard-btn">Discard</Button>
                <Button onClick={applyEnrichment} disabled={applying} className="bg-green-600 hover:bg-green-700 text-white" data-testid="enrichment-apply-btn">
                  {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  Apply Enrichment
                </Button>
              </div>
            </>
          )}
          {step === 'done' && (
            <div className="ml-auto">
              <Button onClick={onClose} data-testid="enrichment-done-btn">Done</Button>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default EnrichmentModal;
